import PropTypes from 'prop-types';
import React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import PortfolioAd from './PortfolioAd';
import Panel from 'components/Panel/Panel';
import { checkUserLocation } from 'helpers/HelperFunctions';
import styles from './PortfolioAdverts.module.scss';
import { PORTFOLIO_AD_ROTATION_MILLISECONDS } from 'constants/constants.js';

import adverts from './AdvertData';
import externalAdvertJobData from './ExternalAdvertJobData';

const advertAnimation = {
  classNames: 'animation-fadein-v1',
  enter: true,
  // transitionAppear: false,
  exit: false,
  timeout: { enter: 500 },
};

const getAdvertArray = (userLocation = null, adLocation) => {
  let advertsToUse;

  if (adLocation === 'external') {
    advertsToUse = externalAdvertJobData;
  } else {
    advertsToUse = adverts;
  }

  let advertArray = [
    ...advertsToUse.ukOnly,
    ...advertsToUse.usOnly,
    ...advertsToUse.worldExcludingAmericaUnitedKingdomAustraliaNewZealand,
    ...advertsToUse.freelancerToolkit,
    ...advertsToUse.globalExcludingIndiaPakistanIraqKenyaNigeriaIran,
  ];

  if (userLocation === null) {
    return advertArray;
  }

  const locationText = userLocation?.toLowerCase();
  const usLocations = ['united states'];
  const indianLocation = ['india'];
  const pakistanLocation = ['pakistan'];
  const iraqLocation = ['iraq'];
  const kenyaLocation = ['kenya'];
  const nigeriaLocation = ['nigeria'];
  const iranLocation = ['iran'];
  const ukLocations = ['united kingdom'];
  const australiaLocations = ['australia'];
  const newZealandLocations = ['new zealand'];
  const eeaLocations = [
    'austria',
    'belgium',
    'bulgaria',
    'croatia',
    'republic of cyprus',
    'czech republic',
    'denmark',
    'estonia',
    'finland',
    'france',
    'germany',
    'greece',
    'hungary',
    'iceland',
    'ireland',
    'italy',
    'latvia',
    'lithuania',
    'malta',
    'netherlands',
    'norway',
    'poland',
    'portugal',
    'romania',
    'slovakia',
    'slovenia',
    'spain',
    'sweden',
  ];

  const globalExcludingIndiaPakistanIraqKenyaNigeriaIran =
    checkUserLocation(locationText, indianLocation) ||
    checkUserLocation(locationText, pakistanLocation) ||
    checkUserLocation(locationText, iraqLocation) ||
    checkUserLocation(locationText, kenyaLocation) ||
    checkUserLocation(locationText, nigeriaLocation) ||
    checkUserLocation(locationText, iranLocation);

  if (checkUserLocation(locationText, usLocations)) {
    // USA
    advertArray = [...advertsToUse.freelancerToolkit, ...advertsToUse.usOnly];
  } else if (globalExcludingIndiaPakistanIraqKenyaNigeriaIran) {
    // EXCLUDING INDIA, PAKISTAN, IRAQ, KENYA, NIGERIA, IRAN
    advertArray = [
      ...advertsToUse.freelancerToolkit,
      ...advertsToUse.worldExcludingAmericaUnitedKingdomAustraliaNewZealand,
    ];
  } else if (checkUserLocation(locationText, eeaLocations)) {
    //EEA
    advertArray = [...advertsToUse.freelancerToolkit, ...advertsToUse.eeaOnly];
  } else if (checkUserLocation(locationText, ukLocations)) {
    // Uk
    advertArray = [...advertsToUse.freelancerToolkit, ...advertsToUse.ukOnly];
  } else if (checkUserLocation(locationText, australiaLocations)) {
    // Australia
    advertArray = [
      ...advertsToUse.freelancerToolkit,
      ...advertsToUse.australiaOnly,
    ];
  } else if (checkUserLocation(locationText, newZealandLocations)) {
    // New Zealand
    advertArray = [
      ...advertsToUse.freelancerToolkit,
      ...advertsToUse.newZealandOnly,
    ];
  } else {
    // Global
    advertArray = [
      ...advertsToUse.freelancerToolkit,
      ...advertsToUse.worldExcludingAmericaUnitedKingdomAustraliaNewZealand,
      ...advertsToUse.globalExcludingIndiaPakistanIraqKenyaNigeriaIran,
    ];
  }

  return advertArray.sort(() => 0.5 - Math.random());
};

const getRandomChoice = (upperLimit = 0, lowerLimit = 0) => {
  return lowerLimit + Math.floor(Math.random() * upperLimit);
};

class PortfolioAdverts extends React.PureComponent {
  state = {
    advertArray: [],
    advertIndex: 0,
    advertData: {},
    hasChosenAdvert: false,
  };

  componentDidMount() {
    this.chooseAdvert();

    this.timer = setInterval(() => {
      this.handleNextAdvert();
    }, PORTFOLIO_AD_ROTATION_MILLISECONDS);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  componentDidUpdate() {
    const { userLocation } = this.props;
    const { hasChosenAdvert, advertData } = this.state;

    if ((!hasChosenAdvert || !advertData) && userLocation) {
      this.chooseAdvert();
    }
  }

  chooseAdvert = () => {
    const { userLocation, adLocation } = this.props;
    const advertArray = getAdvertArray(userLocation, adLocation);

    // Ensure a US specific ad is shown first
    const locationText = userLocation ? userLocation.toLowerCase() : null;

    const usAdLength = adverts.usOnly.length;

    const usIndex = getRandomChoice(usAdLength);

    const globalNotUSIndex = getRandomChoice(advertArray.length);

    const handleIndex = (location) => {
      if (location) {
        if (checkUserLocation(location, ['united states'])) {
          if (usIndex !== -1) {
            return usIndex;
          }
        }
      }
      return globalNotUSIndex;
    };

    const advertChoice = handleIndex(locationText);

    this.setState({
      hasChosenAdvert: true,
      advertArray,
      advertIndex: advertChoice,
      advertData: advertArray[advertChoice],
    });
  };

  handleScrollAdvert = (index) =>
    this.setState({
      advertIndex: index,
      advertData: this.state.advertArray[index],
    });

  handlePrevAdvert = () => {
    const { advertIndex, advertArray } = this.state;
    const newIndex =
      advertIndex - 1 === 0 ? advertArray.length - 1 : advertIndex - 1;

    return this.handleScrollAdvert(newIndex);
  };

  handleNextAdvert = () => {
    const { advertIndex, advertArray } = this.state;

    const newIndex =
      advertIndex + 1 >= advertArray.length ? 0 : advertIndex + 1;

    return this.handleScrollAdvert(newIndex);
  };

  render() {
    const { hasChosenAdvert, advertIndex, advertData } = this.state;
    return hasChosenAdvert && advertData ? (
      <Panel
        backgroundColor={advertData.background}
        className={styles.portfolioAdPanel}
      >
        <TransitionGroup>
          <CSSTransition {...advertAnimation}>
            <div key={advertIndex}>
              <PortfolioAd
                advertData={advertData}
                onNextAdvert={this.handleNextAdvert}
                onPrevAdvert={this.handlePrevAdvert}
              />
            </div>
          </CSSTransition>
        </TransitionGroup>
      </Panel>
    ) : (
      <span />
    );
  }
}

PortfolioAdverts.propTypes = {
  userLocation: PropTypes.string,
  adLocation: PropTypes.string,
};
PortfolioAdverts.defaultProps = {
  userLocation: null,
  adLocation: 'portfolio',
};

export default PortfolioAdverts;
